import { EditOutlined, GiftOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  ConfigProvider,
  Flex,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Skeleton,
  Table,
  Typography,
} from "antd";
import type { TableProps } from "antd";
import { useForm } from "antd/es/form/Form";
import { useState } from "react";
import CardSectionTitle from "components/CardSectionTitle.component";
import { ModalHeader, modalButtonsTheme } from "components/modals/ModalChrome";
import VoucherSection from "components/vouchers/VoucherSection.component";
import { useCustomerLoyalty } from "hooks/queries/loyalty";
import { useAdjustCustomerStamps } from "hooks/mutations/loyalty";
import { useAdminCapabilities } from "hooks/useAdminCapabilities";
import { useTabParam } from "hooks/useTabParam";
import { AdminCapability } from "shared/utils/capabilities";
import { th, tnum } from "shared/components/TableText";
import { PAGE_SIZE } from "shared/constants";
import { translate, translateWithVars } from "shared/languages";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import {
  LOYALTY_CARD_STATUS_LABELS,
  LOYALTY_CARD_STATUS_TONE,
  STAMP_STATUS_LABELS,
  STAMP_STATUS_TONE,
} from "shared/utils";
import { User } from "shared/types";
import { LoyaltyProgramStatus, UserType } from "shared/types/enum";
import {
  CustomerLoyalty,
  CustomerLoyaltyCard,
  CustomerLoyaltyStamp,
} from "shared/types/loyalty";
import { colors } from "shared/theme/color";
import { isUserDeactivated } from "shared/utils";
import { rewardLabel,
  stampReason,
} from "shared/utils/loyalty";
import { DEFAULT_DATE_FORMAT, formatDate, localTzDate } from "shared/utils/dayjs";
import { textPx } from "shared/constants/zoom";

const { Text } = Typography;

// The selector's key for a card: its id, or "new" for the next card, which
// has no row until its first stamp.
const cardKey = (c: CustomerLoyaltyCard) => c.id ?? "new";

// A day as the selector and the status line print it ("12 Sep 2026", in the
// admin's language).
const cardDay = (d: string | null) => (d ? localTzDate(d).format("D MMM YYYY") : "-");

// Every card the client has held, the current (or next) one first. An older
// server sends only the open card; it becomes the one entry.
const cardsOf = (data: CustomerLoyalty | undefined): CustomerLoyaltyCard[] => {
  if (!data) return [];
  if (data.cards) return data.cards;
  return data.cycle
    ? [
        {
          id: null,
          state: "current",
          started_at: data.cycle.started_at,
          expires_at: data.cycle.expires_at,
          ended_at: null,
          stamp_count: data.cycle.stamp_count,
          total_stamps: data.cycle.total_stamps,
          milestones: data.cycle.milestones,
          history: data.history,
        },
      ]
    : [];
};

// One selector row, also shown as the selected value: the card's status (a
// word on a tinted chip, so colour is never the only signal), the day that
// identifies it (a past card by when it ended, the open card by when it
// started), and its progress, right-aligned so the counts line up. The chip
// sits in a fixed slot as wide as the widest status ("Completed", 87px), so
// the days line up down the list whatever the status.
const CARD_STATUS_SLOT = 88;
const CardOption = ({ card }: { card: CustomerLoyaltyCard }) => (
  <Flex align="center" gap={8} style={{ width: "100%", minWidth: 0 }}>
    <span style={{ width: CARD_STATUS_SLOT, flexShrink: 0, display: "inline-flex" }}>
      <StatusBadge
        tone={LOYALTY_CARD_STATUS_TONE[card.state]}
        label={LOYALTY_CARD_STATUS_LABELS[card.state]}
      />
    </span>
    <span
      style={{
        flex: 1,
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {card.state === "new"
        ? translate("loyalty.card.new")
        : card.state === "current"
          ? translateWithVars("loyalty.card.started_on", { date: cardDay(card.started_at) })
          : cardDay(card.ended_at)}
    </span>
    <span style={{ ...tnum, fontWeight: 600, color: colors.ink2 }}>
      {card.stamp_count}/{card.total_stamps}
    </span>
  </Flex>
);

// Customer detail -> Reward tab -> Loyalty Card pane: a card selector (every
// card the client has held, the current one by default; System Review R18),
// the selected card's stamp progress + milestone ladder and stamp history,
// then the client's reward vouchers from every card (shared VoucherSection).
const LoyaltyTab = ({ customer }: { customer: User }) => {
  const customerId = customer.id;
  const { data, isLoading } = useCustomerLoyalty(customerId);
  const adjustStamps = useAdjustCustomerStamps();

  const cards = cardsOf(data);
  const cardKeys = cards.map(cardKey);
  // The selected card rides the URL (`?card=`, written with replace) so Back
  // from a booking or package page returns to the same card. A key that no
  // longer exists (the next card has started since) falls back to the first.
  const { activeKey: selectedKey, onChange: selectCard } = useTabParam(
    cardKeys[0] ?? "new",
    "card",
    cardKeys,
  );
  const selected = cards.find((c) => cardKey(c) === selectedKey) ?? cards[0];
  const viewingPast =
    !!selected && selected.state !== "current" && selected.state !== "new";

  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustForm] = useForm();

  const isDeactivated = isUserDeactivated(customer, UserType.Customer);
  // A manual stamp is a free class in the making: it follows the Manager's
  // "Ubah Kredit" toggle (System Review R1). Plainly disabled, no hover reason.
  const { isRestricted } = useAdminCapabilities();
  const stampsRestricted = isRestricted(AdminCapability.CreditAdjustment);
  const direction = Form.useWatch("direction", adjustForm);

  if (isLoading || !data) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 5 }} />
      </Card>
    );
  }

  const { program, cycle, vouchers } = data;
  // Edit Stamp only while the program runs: an off program refuses every
  // adjustment, so the button used to lead straight to that refusal (R17).
  const programActive = program?.status === LoyaltyProgramStatus.Active;
  // Removing a stamp only ever reaches the OPEN card (a finished card never
  // reopens for it, the 2026-08-21 rule), so with nothing active on it the
  // option is disabled rather than refused on submit (R22, Andrew 2026-09-29).
  const nothingToRemove = (cycle?.stamp_count ?? 0) === 0;
  // When the newest stamp is the one that crossed a milestone, say what
  // removing it does to that reward: an unclaimed voucher is auto-voided
  // (re-earnable), a claimed credit stays and needs Tarik Kredit.
  const crossedMilestone = cycle?.milestones.find(
    (m) => m.stamp_number === cycle.stamp_count && m.achieved
  );
  const crossedVoucher = crossedMilestone
    ? vouchers.find((v) => v.id === crossedMilestone.voucher_id)
    : undefined;
  const removeNote =
    direction === "remove" && crossedVoucher
      ? crossedVoucher.status === "redeemed"
        ? translate("loyalty.adjust.remove_keeps_claimed")
        : crossedVoucher.status === "issued"
          ? translate("loyalty.adjust.remove_voids_reward")
          : null
      : null;

  const onAdjustSubmit = async () => {
    const v = await adjustForm.validateFields().catch(() => null);
    if (!v) return;
    adjustStamps.mutate(
      { customerId, direction: v.direction, reason: v.reason },
      {
        onSuccess: () => {
          setAdjustOpen(false);
          adjustForm.resetFields();
        },
      }
    );
  };

  const historyColumns: TableProps<CustomerLoyaltyStamp>["columns"] = [
    {
      title: th(translate("label.time")),
      dataIndex: "created_at",
      key: "created_at",
      render: (d: string) => (
        <span style={tnum}>
          {formatDate(d, DEFAULT_DATE_FORMAT + ", HH:mm")}
        </span>
      ),
      sorter: (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      defaultSortOrder: "descend",
    },
    {
      title: th(translate("loyalty.col.source")),
      key: "source",
      render: (_, s) => (
        <Flex vertical>
          <span style={{ fontWeight: 600 }}>
            {s.source === "booking"
              ? s.class_name ?? "-"
              : translate("loyalty.history.manual")}
          </span>
          <Text type="secondary" style={{ fontSize: textPx(12) }}>
            {s.source === "booking"
              ? s.occurrence_timestamp
                ? formatDate(
                    s.occurrence_timestamp,
                    DEFAULT_DATE_FORMAT + ", HH:mm"
                  )
                : ""
              : `${s.actor_name ?? ""}${s.reason ? ` · ${s.reason}` : ""}`}
          </Text>
        </Flex>
      ),
    },
    {
      title: th(translate("label.status")),
      key: "status",
      render: (_, s) => {
        // A class no longer attended keeps its stamp on a closed card. On a
        // completed card it goes if the card reopens (a reward of it is
        // voided); on a lapsed card it stays for good, so it still reads as
        // Counted there and the reason says why.
        const status = s.voided
          ? "voided"
          : s.class_reverted && selected?.state === "completed"
            ? "pending_revert"
            : "counted";
        return (
          <StatusBadge tone={STAMP_STATUS_TONE[status]} label={STAMP_STATUS_LABELS[status]} />
        );
      },
    },
    {
      title: th(translate("loyalty.col.reason")),
      key: "reason",
      render: (_: unknown, row: CustomerLoyaltyStamp) => {
        if (!row.voided && row.class_reverted) {
          return (
            <Text type="secondary" style={{ fontSize: textPx(13) }}>
              {translate(
                selected?.state === "completed"
                  ? "loyalty.reason.class_reverted_completed"
                  : "loyalty.reason.class_reverted_closed",
              )}
            </Text>
          );
        }
        const r = stampReason(row);
        if (!r.text) return <Text type="secondary">-</Text>;
        return (
          <Flex vertical>
            <Text
              style={{ fontSize: textPx(13) }}
              type={r.system ? "secondary" : undefined}
            >
              {r.text}
            </Text>
            {r.actor && (
              <Text type="secondary" style={{ fontSize: textPx(12) }}>
                {r.actor}
              </Text>
            )}
          </Flex>
        );
      },
    },
  ];

  // The selected card's own end, said in one line under its progress. The
  // open card states its deadline (if it has one), the next card when it
  // starts.
  const cardStatusLine = (() => {
    if (!selected) return null;
    if (selected.state === "current") {
      return selected.expires_at ? (
        <Text type="secondary" style={{ fontSize: textPx(13) }}>
          {translate("loyalty.progress.cycle_deadline")}{" "}
          <strong>{formatDate(selected.expires_at, DEFAULT_DATE_FORMAT)}</strong>
        </Text>
      ) : null;
    }
    const key =
      selected.state === "new"
        ? "loyalty.card.new_note"
        : selected.state === "completed"
          ? "loyalty.card.completed_on"
          : selected.state === "expired"
            ? "loyalty.card.expired_on"
            : "loyalty.card.ended_on";
    return (
      <Text type="secondary" style={{ fontSize: textPx(13) }}>
        {translateWithVars(key, { date: cardDay(selected.ended_at) })}
      </Text>
    );
  })();

  return (
    <Flex vertical gap={16}>
      {/* ---- Card selector: only once there is a past card to pick ---- */}
      {cards.length > 1 && (
        <Flex align="center" gap={12} wrap="wrap">
          <Text strong id="loyalty-card-select-label">
            {translate("loyalty.card.label")}
          </Text>
          <Select
            aria-labelledby="loyalty-card-select-label"
            value={selectedKey}
            onChange={selectCard}
            // Wide enough for the longest closed value ("Started 30 Sep 2026",
            // 10/10, measured) and no wider, so the chip, the day and the
            // count stay together.
            style={{ width: 330, maxWidth: "100%" }}
            options={cards.map((c) => ({
              value: cardKey(c),
              label: <CardOption card={c} />,
            }))}
          />
        </Flex>
      )}

      {/* ---- Stamp progress (selected card) ---- */}
      <Card>
        <Flex vertical gap={18}>
          <Flex justify="space-between" align="flex-start" gap={16} wrap="wrap">
            <CardSectionTitle
              title={translate("loyalty.progress.title")}
              subtitle={translate("loyalty.progress.subtitle")}
            />
            <ConfigProvider theme={modalButtonsTheme}>
              <Button
                type="primary"
                icon={<EditOutlined />}
                // Read-only while the client is deactivated here. A stamp
                // only ever lands on the current card, so a past card
                // selected leaves nothing to edit here.
                disabled={
                  !programActive || isDeactivated || stampsRestricted || viewingPast
                }
                onClick={() => setAdjustOpen(true)}
              >
                {translate("loyalty.action.adjust")}
              </Button>
            </ConfigProvider>
          </Flex>

          {!program && cards.length === 0 && (
            <Text type="secondary">
              {translate("loyalty.progress.no_program")}
            </Text>
          )}
          {program && program.status !== LoyaltyProgramStatus.Active && (
            <Alert
              type="info"
              showIcon
              message={translate("loyalty.progress.program_off")}
            />
          )}
          {(selected || program) && (
            <Flex align="baseline" gap={10} wrap="wrap">
              <span
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: textPx(34),
                  fontWeight: 700,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {selected?.stamp_count ?? 0}
                <Text
                  type="secondary"
                  style={{ fontSize: textPx(18), fontWeight: 500 }}
                >
                  {" "}
                  / {selected?.total_stamps ?? program?.total_stamps}{" "}
                  {translate("loyalty.progress.stamp_unit")}
                </Text>
              </span>
              {cardStatusLine}
            </Flex>
          )}
          {selected && selected.milestones.length > 0 && (
            <Flex gap={8} wrap="wrap">
              {selected.milestones.map((m) => (
                <div
                  key={m.stamp_number}
                  style={{
                    border: `1px solid ${
                      m.achieved ? colors.navy : colors.line
                    }`,
                    background: m.achieved ? colors.navyTint : colors.card,
                    borderRadius: 10,
                    padding: "6px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <GiftOutlined
                    style={{
                      color: m.achieved ? colors.navyDark : colors.ink3,
                    }}
                  />
                  <Flex vertical>
                    <Text style={{ fontSize: textPx(12), fontWeight: 600 }}>
                      {m.stamp_number}{" "}
                      {translate("loyalty.progress.stamp_unit")}
                    </Text>
                    <Text type="secondary" style={{ fontSize: textPx(12) }}>
                      {rewardLabel(m)}
                    </Text>
                  </Flex>
                </div>
              ))}
            </Flex>
          )}
        </Flex>
      </Card>

      {/* ---- Stamp history (selected card) ---- */}
      <Card>
        <Flex vertical gap={18}>
          <CardSectionTitle
            title={translate("loyalty.history.title")}
            subtitle={translate("loyalty.history.subtitle")}
          />
          <Table
            showSorterTooltip={false}
            // A fresh table per card, so the page it shows starts at 1.
            key={selected ? cardKey(selected) : "none"}
            rowKey="id"
            columns={historyColumns}
            dataSource={selected?.history ?? []}
            pagination={{ pageSize: PAGE_SIZE, showSizeChanger: false }}
            locale={{ emptyText: translate("loyalty.history.empty") }}
          />
        </Flex>
      </Card>

      {/* ---- Vouchers from every card (shared section: table +
          claim/void/claw-back). Below the selected card's sections on
          purpose: the selector does not filter it. ---- */}
      <VoucherSection
        customerId={customerId}
        vouchers={vouchers}
        isDeactivated={isDeactivated}
        title={translate("loyalty.vouchers.title")}
        subtitle={translate("loyalty.vouchers.subtitle")}
        emptyText={translate("loyalty.vouchers.empty")}
      />

      {/* ---- Manual adjust modal ---- */}
      <ConfigProvider theme={modalButtonsTheme}>
        <Modal
          open={adjustOpen}
          onOk={onAdjustSubmit}
          onCancel={() => setAdjustOpen(false)}
          okText={translate("button.confirm")}
          cancelText={translate("button.cancel")}
          confirmLoading={adjustStamps.isPending}
          afterClose={() => adjustForm.resetFields()}
          title={
            <ModalHeader
              eyebrow={translate("loyalty.progress.title")}
              title={translate("loyalty.adjust.title")}
              tone="amber"
            />
          }
        >
          <Form
            form={adjustForm}
            layout="vertical"
            initialValues={{ direction: "add" }}
          >
            <Form.Item
              name="direction"
              label={translate("loyalty.adjust.direction")}
            >
              <Radio.Group
                options={[
                  { value: "add", label: translate("loyalty.adjust.add") },
                  {
                    value: "remove",
                    label: translate("loyalty.adjust.remove"),
                    disabled: nothingToRemove,
                  },
                ]}
              />
            </Form.Item>
            {removeNote && (
              <Text
                type="secondary"
                style={{ display: "block", fontSize: textPx(13), marginTop: -12, marginBottom: 16 }}
              >
                {removeNote}
              </Text>
            )}
            <Form.Item
              name="reason"
              label={translate("loyalty.adjust.reason")}
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: translate("loyalty.adjust.reason_required"),
                },
              ]}
            >
              <Input.TextArea
                rows={2}
                placeholder={translate("loyalty.adjust.reason_placeholder")}
              />
            </Form.Item>
          </Form>
        </Modal>
      </ConfigProvider>

    </Flex>
  );
};

export default LoyaltyTab;
